import { DomainSettingsForm } from "@/components/app/project-settings/DomainSettingsForm";
import { editorClient } from "@/lib/editorClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { Trans } from "@lingui/react/macro";
import { redirect, notFound } from "@tanstack/react-router";
import { DefaultDomain } from "@/components/app/project-settings/DefaultDomain";
import { GeneralSettingsForm } from "@/components/app/project-settings/GeneralSettingsForm";

async function getDomainSettingsData({
  organizationSlug,
  projectSlug,
}: ParamsSchema) {
  const res = await editorClient.projects[":organizationSlug"][
    ":projectSlug"
  ].settings.domain.$get({
    param: {
      organizationSlug,
      projectSlug,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get domain settings");
  }
  const domain = await res.json();
  return domain;
}

async function getGeneralSettingsData({
  organizationSlug,
  projectSlug,
}: ParamsSchema) {
  const res = await editorClient.projects[":organizationSlug"][
    ":projectSlug"
  ].settings.general.$get({
    param: {
      organizationSlug,
      projectSlug,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get general settings");
  }
  const generalSettings = await res.json();
  return generalSettings;
}

const domainSettingsQueryKey = (params: ParamsSchema) => [
  "editor-project-settings-domain",
  params,
];

const generalSettingsQueryKey = (params: ParamsSchema) => [
  "editor-project-settings-general",
  params,
];

const paramsSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
});

type ParamsSchema = z.infer<typeof paramsSchema>;

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/settings"
)({
  component: RouteComponent,
  params: paramsSchema,
  loader: async ({ params, context: { queryClient } }) => {
    const generalSettings = await getGeneralSettingsData(params);
    queryClient.setQueryData(generalSettingsQueryKey(params), generalSettings);
    const domains = await getDomainSettingsData(params);
    queryClient.setQueryData(domainSettingsQueryKey(params), domains);
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const queryClient = useQueryClient();
  const { organizationSlug, projectSlug } = params;

  const { data: domainSettingsData } = useQuery(
    {
      queryKey: domainSettingsQueryKey(params),
      queryFn: () => getDomainSettingsData(params),
    },
    queryClient
  );

  const { data: generalSettings } = useQuery(
    {
      queryKey: generalSettingsQueryKey(params),
      queryFn: () => getGeneralSettingsData(params),
    },
    queryClient
  );

  return (
    <div className="container max-w-none flex w-full flex-col items-center gap-6 bg-default-background py-12 overflow-auto">
      <div className="flex w-full max-w-[576px] flex-col items-start gap-6">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="w-full text-heading-2 font-heading-2 text-default-font">
            <Trans>Project Settings</Trans>
          </span>
          <span className="text-body font-body text-subtext-color">
            <Trans>Project settings for {projectSlug}</Trans>
          </span>
        </div>
        <GeneralSettingsForm
          generalSettings={generalSettings ?? null}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
        />
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        <DefaultDomain
          defaultDomain={domainSettingsData?.defaultDomain ?? ""}
        />
        <DomainSettingsForm
          domainSettings={domainSettingsData?.customDomain ?? null}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
        />
      </div>
    </div>
  );
}
